import { OvertimeRequest } from '@/types'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { OvertimeStatus } from '@/types/enums'

/**
 * Hook for checking employee overtime eligibility
 */
export const useEligibilityCheck = (overtimeRequests: OvertimeRequest[]) => {
  const checkEmployeeOvertimeEligibility = async (
    employeeId: string, 
    hours: number
  ) => {
    try {
      // Note: Shift validation is now handled by the backend add_employee_overtime_unified function
      // which properly checks working hours against the shifts table

      // Default max overtime hours if we can't query the database
      let maxOvertime = 104;
      
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('max_overtime_hours')
          .eq('is_deleted', false)
          .limit(1)
        
        if (error) {
          throw error;
        }
        
        if (data && data[0] && 'max_overtime_hours' in data[0]) {
          maxOvertime = (data[0] as any).max_overtime_hours;
        }
      } catch {
        // Continue with default maxOvertime
      }
      
      // Calculate monthly hours from context data to avoid another query
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const currentEmployeeRequests = overtimeRequests.filter(req => 
        req.employeeId === employeeId && req.status === OvertimeStatus.APPROVED
      );
      
      const monthlyHours = currentEmployeeRequests.reduce((total, req) => {
        const reqDate = new Date(req.date);
        if (reqDate.getMonth() === currentMonth && reqDate.getFullYear() === currentYear) {
          return total + req.hours;
        }
        return total;
      }, 0);
      
      return { 
        requiresVPApproval: (monthlyHours + hours) > maxOvertime,
        currentMonthlyHours: monthlyHours,
        maxAllowedHours: maxOvertime,
        isAllowed: true
      };
    } catch {
      toast.error("Error checking overtime eligibility");
      // Return default values conforming to the expected type
      return { requiresVPApproval: false, maxAllowedHours: 0, isAllowed: true };
    }
  };
  
  return { checkEmployeeOvertimeEligibility };
};
